/**
 * Copyright (c) 2022, ResMed Inc. All rights reserved.
 */
import Paper from "@material-ui/core/Paper";
import FormGroup from "@material-ui/core/FormGroup";
import Typography from "@material-ui/core/Typography";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox"
import {v4 as uuidv4} from "uuid";
import AddIcon from "@material-ui/icons/Add";
import {React, useEffect, useState} from "react";
import '../../../css/views/editors/SourceIdEditor.css'
import '../../../css/views/Views.css'

/**
 * Component representing the source id editor.
 *
 * @prop sourceId       source id to edit (leave empty for default)
 * @prop updateSourceId function to call on source id change
 */
export default function SourceIdEditor(props) {

    const PATIENT_PREFIX = "PATIENT_ECN";
    const DEVICE_PREFIX  = "DEVICE_SERIAL";

    let sourceId = props.sourceId;

    /**
     * Grabs prefix of source id, or sets it to default if it doesn't exist.
     *
     * @return prefix
     */
    const getSourceIdPrefix = () => {
        let prefix = PATIENT_PREFIX;

        if (sourceId) {
            prefix = sourceId.substring(0, sourceId.indexOf('#'));

            // ensure the prefix is a valid prefix
            if (prefix !== PATIENT_PREFIX && prefix !== DEVICE_PREFIX) {
                prefix = PATIENT_PREFIX;
            }
        }

        return prefix;
    }
    const [sourceIdPrefix, setSourceIdPrefix] = useState(getSourceIdPrefix);

    /**
     * Gets value after '#' in source id
     *
     * @return source id value
     */
    const getSourceIdValue = () => {
        let value = "";

        if (sourceId) {
            value = sourceId.substring(sourceId.indexOf('#') + 1);
        }

        return value;
    }
    const [sourceIdValue, setSourceIdValue] = useState(getSourceIdValue);

    /**
     * Handles change in radio group
     *
     * @param event from radio group
     */
    const handleRadioChange = (event) => {
        const newPrefix = event.target.value;

        setSourceIdPrefix(newPrefix);
    }

    const handleCheckboxChange = (event) => {
        props.updateRequirePatientConsent()
    }

    /**
     * Handle text field change
     *
     * @param event from text field
     */
    const handleTextFieldChange = (event) => {
        const newSourceIdValue = event.target.value;

        setSourceIdValue(newSourceIdValue);
    }

    /**
     * Handles generating random id for source id
     */
    const handleGenerateId = () => {
        const newSourceIdValue = uuidv4();

        setSourceIdValue(newSourceIdValue);
    }

    /**
     * On any changes in form, update the sourceId.
     */
    useEffect(() => {
        sourceId = sourceIdPrefix + "#" + sourceIdValue;
        props.updateSourceId(sourceId);
    }, [sourceIdPrefix, sourceIdValue])

    return (
        <Paper elevation={4} className='sourceIdEditor-paper'>
            <FormGroup>
                <Typography variant="button" className='sourceIdEditor-header' gutterBottom>
                    Source ID
                </Typography>
                <RadioGroup
                    row aria-label="position"
                    name="position"
                    value={sourceIdPrefix}
                    onChange={handleRadioChange}
                >
                    <FormControlLabel
                        value={PATIENT_PREFIX}
                        control={<Radio color="primary" />}
                        label="Patient ECN"
                        labelPlacement="start"
                    />
                    <FormControlLabel
                        value={DEVICE_PREFIX}
                        control={<Radio color="primary" />}
                        label="Device Serial"
                        labelPlacement="start"
                    />
                </RadioGroup>
                <RadioGroup row aria-label="position">
                    <FormControlLabel
                        label="Require Patient Consent:"
                        control={<Checkbox color="primary"/>}
                        labelPlacement="start"
                        onChange={props.updateRequirePatientConsent}
                    />
                </RadioGroup>
                <TextField
                    id="sourceId"
                    label="Source ID"
                    variant="outlined"
                    value={sourceIdValue}
                    onChange={handleTextFieldChange}
                />
                <Button
                    variant="outlined"
                    color="primary"
                    className='sourceIdEditor-button'
                    onClick={handleGenerateId}
                    startIcon={<AddIcon />}
                >
                    Generate UUID for source id
                </Button>
            </FormGroup>
        </Paper>
    );
}
